import { toast } from "react-toastify";
import { useExtensionContext } from "app/popleads/modules/extension/ExtensionContext";

// The profile keeps two email fields; a suggestion goes to the first empty one.
const EMAIL_FIELDS = ["email", "email2"];

/**
 * Suggestions still worth showing: not already one of the profile's emails. Checked against the current profile,
 * so a row disappears as soon as it is saved, without fetching the suggestions again.
 */
export const unsavedSuggestions = (suggestions, profile) => {
    const own = EMAIL_FIELDS.map((f) =>
        String(profile?.[f] || "")
            .trim()
            .toLowerCase()
    ).filter(Boolean);
    return suggestions.filter((s) => !s.matchesProfileEmail && !own.includes(s.email.toLowerCase()));
};

const REJECTED = ["invalid", "disposable"];

/**
 * The one suggestion to show, so the card never turns into a list of guesses. The backend verifies the account's
 * patterns in order until one is verified, so: a verified address wins; otherwise the best candidate the verifier
 * did not reject (still being checked, or unconfirmed); when every candidate was rejected, the first one, shown as
 * failed. Addresses owned by another profile are never shown. Once one suggestion is saved to the profile (email
 * or email2), the user has chosen, so no suggestion is shown.
 */
export const shownSuggestions = (suggestions, profile) => {
    const unsaved = unsavedSuggestions(suggestions, profile);
    if (unsaved.length < suggestions.length) return [];
    const candidates = unsaved.filter((s) => !s.ownedByOtherProfile);
    const best =
        candidates.find((s) => s.verification?.status === "ok") ||
        candidates.find((s) => !REJECTED.includes(s.verification?.status)) ||
        candidates[0];
    return best ? [best] : [];
};

const parseShares = (shares) => {
    if (!shares) return {};
    if (typeof shares === "object") return shares;
    try {
        return JSON.parse(shares) || {};
    } catch (e) {
        return {};
    }
};

/**
 * How common a suggestion's pattern is at its company, from the account's measurement (account_email_pattern):
 * fitRate = share of the known named addresses the patterns reproduce, knownAddressCount = how many they reproduce,
 * patternShares = each pattern's share of those. So share x fitRate of all knownAddressCount / fitRate known
 * addresses use this pattern. Null when the account has no such measurement.
 *
 * @returns {{ percent: number, known: number } | null}
 */
export const patternLikelihood = (emailPattern, pattern) => {
    const fit = Number(emailPattern?.fitRate);
    const covered = Number(emailPattern?.knownAddressCount);
    if (!pattern || !(fit > 0) || !(covered > 0)) return null;
    const wanted = String(pattern)
        .trim()
        .toLowerCase();
    const entry = Object.entries(parseShares(emailPattern.patternShares)).find(
        ([p]) => p.trim().toLowerCase() === wanted
    );
    const share = Number(entry?.[1]);
    if (!entry || !(share >= 0)) return null;
    return { percent: Math.round(share * fit * 100), known: Math.round(covered / fit) };
};

/**
 * One-click save of an AI-suggested email into the profile: email when empty, else email2.
 * When both are taken the user is asked to remove one first; nothing is overwritten.
 *
 * @returns {(email: string) => Promise<void>}
 */
const useSaveSuggestedEmail = () => {
    const { profile, handleUpdateProfile } = useExtensionContext();

    return async (email) => {
        const field = EMAIL_FIELDS.find((f) => !profile?.[f] || !String(profile[f]).trim());
        if (!field) {
            toast.warning("This profile already has 2 emails. Remove one to save the suggested email.");
            return;
        }
        const result = await handleUpdateProfile({ field, fieldValue: email });
        if (result?.error) {
            toast.error(typeof result.error === "string" ? result.error : "The email could not be saved");
            return;
        }
        toast.success("Suggested email saved to the profile");
    };
};

export default useSaveSuggestedEmail;
